import React, { createContext, useReducer } from 'react';
import { UIState, ToastType } from '../types';

// Initial state
const initialState: UIState = {
  loadingStates: {},
  errorStates: {},
  modalStates: {},
  toastState: {
    type: 'info',
    message: '',
    visible: false,
    title: '',
  },
};

// Action types
type UIAction =
  | { type: 'SET_LOADING'; payload: { key: string; isLoading: boolean } }
  | { type: 'SET_ERROR'; payload: { key: string; error: Error | null } }
  | { type: 'SHOW_MODAL'; payload: string }
  | { type: 'HIDE_MODAL'; payload: string }
  | { type: 'SHOW_TOAST'; payload: { type: ToastType; message: string; title?: string } }
  | { type: 'HIDE_TOAST' };

// Reducer
const uiReducer = (state: UIState, action: UIAction): UIState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loadingStates: {
          ...state.loadingStates,
          [action.payload.key]: action.payload.isLoading,
        },
      };
    case 'SET_ERROR':
      return {
        ...state,
        errorStates: {
          ...state.errorStates,
          [action.payload.key]: action.payload.error,
        },
      };
    case 'SHOW_MODAL':
      return {
        ...state,
        modalStates: {
          ...state.modalStates,
          [action.payload]: true,
        },
      };
    case 'HIDE_MODAL':
      return {
        ...state,
        modalStates: {
          ...state.modalStates,
          [action.payload]: false,
        },
      };
    case 'SHOW_TOAST':
      return {
        ...state,
        toastState: {
          type: action.payload.type,
          message: action.payload.message,
          title: action.payload.title || '',
          visible: true,
        },
      };
    case 'HIDE_TOAST':
      return {
        ...state,
        toastState: {
          ...state.toastState,
          visible: false,
        },
      };
    default:
      return state;
  }
};

// Context
interface UIContextType extends UIState {
  setLoading: (key: string, isLoading: boolean) => void;
  setError: (key: string, error: Error | null) => void;
  showModal: (key: string) => void;
  hideModal: (key: string) => void;
  showToast: (type: ToastType, message: string, title?: string) => void;
  hideToast: () => void;
  isLoading: (key: string) => boolean;
  getError: (key: string) => Error | null;
  isModalVisible: (key: string) => boolean;
}

export const UIContext = createContext<UIContextType | undefined>(undefined);

// Provider
interface UIProviderProps {
  children: React.ReactNode;
}

export const UIProvider: React.FC<UIProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(uiReducer, initialState);

  // Set loading state
  const setLoading = (key: string, isLoading: boolean): void => {
    dispatch({ type: 'SET_LOADING', payload: { key, isLoading } });
  };

  // Set error state
  const setError = (key: string, error: Error | null): void => {
    dispatch({ type: 'SET_ERROR', payload: { key, error } });
  };

  // Show modal
  const showModal = (key: string): void => {
    dispatch({ type: 'SHOW_MODAL', payload: key });
  };

  // Hide modal
  const hideModal = (key: string): void => {
    dispatch({ type: 'HIDE_MODAL', payload: key });
  };

  // Show toast
  const showToast = (type: ToastType, message: string, title?: string): void => {
    dispatch({ type: 'SHOW_TOAST', payload: { type, message, title } });
    // Auto-hide toast after 5 seconds
    setTimeout(() => {
      hideToast();
    }, 5000);
  };

  // Hide toast
  const hideToast = (): void => {
    dispatch({ type: 'HIDE_TOAST' });
  };

  // Check if a key is loading
  const isLoading = (key: string): boolean => {
    return !!state.loadingStates[key];
  };

  // Get error for a key
  const getError = (key: string): Error | null => {
    return state.errorStates[key] || null;
  };

  // Check if a modal is visible
  const isModalVisible = (key: string): boolean => {
    return !!state.modalStates[key];
  };

  return (
    <UIContext.Provider
      value={{
        ...state,
        setLoading,
        setError,
        showModal,
        hideModal,
        showToast,
        hideToast,
        isLoading,
        getError,
        isModalVisible,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};